import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  Text,
  FormRow,
  Input,
  Checkbox,
  Search
} from '@efast_public/fjd-component-library';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import {
  GovOsResult,
  ManagedOnlineService,
  UpdateZuFiServiceRequest,
  GebietSearchResponse,
  LeistungSearchResponse,
  Gebiet,
  Leistung,
  Zufi
} from '../api/govos.types';
import { useNotification } from '../shared/notification/useNotification';
import {
  GovosApiRoutes,
  govosClient,
  invalidateConfiguredOnlinediensteCache
} from '../api/govos-api';
import { debounceAsync, DEFAULT_DEBOUNCE_DELAY } from '../utils/debounce';
import { createJoiWithTranslations } from '../utils/joiConfig';

interface EditZuFiConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onlinedienst: ManagedOnlineService;
}

type ZuFiFormData = {
  gebietId: string;
  gebietName: string;
  gebietType: string;
  leistungId: string;
  leistungName: string;
  linkTitle: string;
  shouldRelease: boolean;
};

const getDefaultValues = (zufi: Zufi): ZuFiFormData => ({
  gebietId: zufi?.gebietId || '',
  gebietName: zufi?.gebietName || '',
  gebietType: zufi?.gebietType || '',
  leistungId: zufi?.leistungId || '',
  leistungName: zufi?.leistungName || '',
  linkTitle: zufi?.linkTitle || '',
  shouldRelease: zufi?.shouldRelease || false
});

export function EditZuFiConfigModal({
  isOpen,
  onClose,
  onlinedienst
}: EditZuFiConfigModalProps) {
  const { t } = useTranslation();
  const { notifyUserFromRequest } = useNotification();
  const queryClient = useQueryClient();
  const CustomJoi = createJoiWithTranslations(t);

  const validationSchema = CustomJoi.object<ZuFiFormData>({
    gebietId: CustomJoi.string().required(),
    gebietName: CustomJoi.string().optional().allow(''),
    gebietType: CustomJoi.string().optional().allow(''),
    leistungId: CustomJoi.string().required(),
    leistungName: CustomJoi.string().optional().allow(''),
    linkTitle: CustomJoi.string().required(),
    shouldRelease: CustomJoi.boolean().optional()
  });

  const { register, handleSubmit, formState, control, watch, setValue } =
    useForm<ZuFiFormData>({
      defaultValues: getDefaultValues(onlinedienst.editableContainer.zufi),
      resolver: joiResolver(validationSchema),
      mode: 'onChange'
    });

  const formValues = watch();
  const canPublish =
    formValues.gebietId && formValues.leistungId && formValues.linkTitle;

  const { mutate: updateZuFiConfig, isPending } = useMutation({
    mutationFn: async (data: ZuFiFormData) => {
      const payload: UpdateZuFiServiceRequest = {
        gebietId: data.gebietId,
        gebietName: data.gebietName,
        gebietType: data.gebietType,
        leistungId: data.leistungId,
        leistungName: data.leistungName,
        linkTitle: data.linkTitle,
        shouldRelease: data.shouldRelease
      };
      return notifyUserFromRequest(
        () =>
          govosClient.put<GovOsResult<unknown>>(
            GovosApiRoutes.zufiService(
              onlinedienst.startAppId,
              onlinedienst.processId
            ),
            payload
          ),
        {
          successMessage: t('EditZuFiConfigModal.successMessage'),
          errorMessage: t('EditZuFiConfigModal.errorMessage'),
          loadingMessage: t('EditZuFiConfigModal.loadingMessage')
        }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: invalidateConfiguredOnlinediensteCache(
          onlinedienst.processId ?? ''
        )
      });
      void queryClient.invalidateQueries({
        queryKey: [
          GovosApiRoutes.zufiService(
            onlinedienst.startAppId,
            onlinedienst.processId
          )
        ]
      });
      onClose();
    }
  });

  const handleSave = handleSubmit((data) => {
    updateZuFiConfig(data);
  });

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('EditZuFiConfigModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <FormRow
            label={t('EditZuFiConfigModal.fields.gebiet')}
            tooltip={t('EditZuFiConfigModal.fields.gebietHint')}
            validationMessage={formState.errors.gebietId?.message}
            variant={formState.errors.gebietId ? 'error' : undefined}
          >
            <Controller
              name="gebietId"
              control={control}
              render={({ field }) => (
                <Search
                  {...field}
                  selectType="single"
                  defaultOptions={
                    onlinedienst.editableContainer.zufi?.gebietId
                      ? [
                          {
                            value: onlinedienst.editableContainer.zufi.gebietId,
                            label: `${onlinedienst.editableContainer.zufi.gebietName} (${onlinedienst.editableContainer.zufi.gebietType})`
                          }
                        ]
                      : []
                  }
                  options={[]}
                  placeholder="Gebiet suchen..."
                  defaultAjaxErrorText={t('select.searchErrorText')}
                  ajax={debounceAsync(async (search: string) => {
                    const response =
                      await govosClient.get<GebietSearchResponse>(
                        GovosApiRoutes.zufiService(
                          onlinedienst.startAppId,
                          onlinedienst.processId,
                          'gebiet',
                          search
                        )
                      );
                    return response.data.gebiete.map((gebiet) => ({
                      value: gebiet.id.toString(),
                      label: `${gebiet.bezeichnung} (${gebiet.typBezeichnung})`,
                      valueObject: gebiet
                    }));
                  }, DEFAULT_DEBOUNCE_DELAY)}
                  renderItemsFoundText={(foundItems) => {
                    return foundItems
                      ? t('select.itemsFoundText', { count: foundItems })
                      : t('select.noItemsFoundText');
                  }}
                  onChange={(value) => {
                    const gebiet = value.at(0)?.valueObject as Gebiet;
                    if (gebiet) {
                      field.onChange(value.at(0)?.value);
                      setValue('gebietName', gebiet.bezeichnung);
                      setValue('gebietType', gebiet.typBezeichnung);
                    } else {
                      field.onChange('');
                      setValue('gebietName', '');
                      setValue('gebietType', '');
                    }
                  }}
                />
              )}
            />
          </FormRow>

          <FormRow
            label={t('EditZuFiConfigModal.fields.leistung')}
            tooltip={t('EditZuFiConfigModal.fields.leistungHint')}
            validationMessage={formState.errors.leistungId?.message}
            variant={formState.errors.leistungId ? 'error' : undefined}
          >
            <Controller
              name="leistungId"
              control={control}
              render={({ field }) => (
                <Search
                  {...field}
                  selectType="single"
                  placeholder="Leistung suchen..."
                  options={[]}
                  defaultOptions={
                    onlinedienst.editableContainer.zufi?.leistungId
                      ? [
                          {
                            value:
                              onlinedienst.editableContainer.zufi.leistungId,
                            label:
                              onlinedienst.editableContainer.zufi.leistungName
                          }
                        ]
                      : []
                  }
                  defaultAjaxErrorText={t('select.searchErrorText')}
                  renderItemsFoundText={(foundItems) => {
                    return foundItems
                      ? t('select.itemsFoundText', { count: foundItems })
                      : t('select.noItemsFoundText');
                  }}
                  ajax={debounceAsync(async (search: string) => {
                    const response =
                      await govosClient.get<LeistungSearchResponse>(
                        GovosApiRoutes.zufiService(
                          onlinedienst.startAppId,
                          onlinedienst.processId,
                          'leistung',
                          search
                        )
                      );
                    return response.data.leistungen.map((leistung) => ({
                      value: leistung.id.toString(),
                      label: `${leistung.bezeichnung}`,
                      valueObject: leistung
                    }));
                  }, DEFAULT_DEBOUNCE_DELAY)}
                  onChange={(value) => {
                    const leistung = value.at(0)?.valueObject as Leistung;
                    if (leistung) {
                      field.onChange(value.at(0)?.value);
                      setValue('leistungName', leistung.bezeichnung);
                    } else {
                      field.onChange('');
                      setValue('leistungName', '');
                    }
                  }}
                />
              )}
            />
          </FormRow>

          <FormRow
            label={t('EditZuFiConfigModal.fields.linkText')}
            tooltip={t('EditZuFiConfigModal.fields.linkTextHint')}
            validationMessage={formState.errors.linkTitle?.message}
            variant={formState.errors.linkTitle ? 'error' : undefined}
          >
            <Input {...register('linkTitle')} style={{ width: '100%' }} />
          </FormRow>

          {/* TODO: TBD Klären mit UX */}
          <FormRow
            tooltip={t('EditZuFiConfigModal.fields.veroeffentlichenTooltip')}
            label={t('EditZuFiConfigModal.fields.veroeffentlichen')}
          >
            <Controller
              name="shouldRelease"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onChange={(checked) => field.onChange(checked)}
                  label={t(
                    'EditZuFiConfigModal.fields.veroeffentlichenCheckbox'
                  )}
                  disabled={!canPublish}
                />
              )}
            />
          </FormRow>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '12px',
            width: '100%'
          }}
        >
          <Button
            variant="secondary-outline"
            onClick={onClose}
            disabled={isPending}
            style={{ width: '100%' }}
          >
            {t('EditZuFiConfigModal.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            disabled={!formState.isValid || isPending}
            style={{ width: '100%' }}
          >
            {t('EditZuFiConfigModal.actions.save')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
